import { List } from '@services/member/list/list.types';
import { useGetMembersMutation, useGetArchivedMembersMutation, useToggleArchiveMemberMutation, useGetMembersExportMutation, useGetArchivedMembersExportMutation } from '@services/member/member/member.endpoints';
import { MemberTable } from '@services/member/member/member.types';
import { useGetStateCodesMutation, useGetOrganizationCodesMutation, useGetServiceCodesMutation } from '@services/member/list/list.endpoints';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { useSelector } from 'react-redux';
import { getUser } from '@features/auth/auth.slice';

export const useListPageVM = () => {

    const MySwal = withReactContent(Swal);

    const [totalMembers, setTotalMembers] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const [stateCodes, setStateCodes] = useState<List[]>([]);
    const [organizationCodes, setOrganizationCodes] = useState<List[]>([]);
    const [serviceCodes, setServiceCodes] = useState<List[]>([]);
    const columnHelper = createColumnHelper<MemberTable>();
    const user = useSelector(getUser);
    const navigate = useNavigate();
    const [, setSearchParams] = useSearchParams();
    const [needReload, setNeedReload] = useState<boolean>();

    const [toggleArchiveMember, { isSuccess: isToggleArchiveMemberSuccess, isLoading: isToggleArchiveMemberLoading }] = useToggleArchiveMemberMutation();
    const [getStateCodes, { isSuccess: isGetStateCodesSuccess, isLoading: isGetStateCodesLoading, data: stateCodesData }] = useGetStateCodesMutation();
    const [getOrganizationCodes, { isSuccess: isGetOrganizationCodesSuccess, isLoading: isGetOrganizationCodesLoading, data: organizationCodesData }] = useGetOrganizationCodesMutation();
    const [getServiceCodes, { isSuccess: isGetServiceCodesSuccess, isLoading: isGetServiceCodesLoading, data: serviceCodesData }] = useGetServiceCodesMutation();

    const mutationHook = useMemo(() => {
        return isArchived ? useGetArchivedMembersMutation : useGetMembersMutation;
    }, [isArchived]);

    const exportMutationHook = useMemo(() => {
        return isArchived ? useGetArchivedMembersExportMutation : useGetMembersExportMutation;
    }, [isArchived]);

    useEffect(() => {
        getStateCodes(undefined);
        getOrganizationCodes(undefined);
        getServiceCodes(undefined);
    }, []);

    useEffect(() => {
        if(isGetStateCodesLoading) return;
        if (isGetStateCodesSuccess && stateCodesData) {
            setStateCodes(stateCodesData.data);
        }
    }, [isGetStateCodesSuccess]);

    useEffect(() => {
        if(isGetOrganizationCodesLoading) return;
        if (isGetOrganizationCodesSuccess && organizationCodesData) {
            setOrganizationCodes(organizationCodesData.data);
        }
    }, [isGetOrganizationCodesSuccess]);

    useEffect(() => {
        if(isGetServiceCodesLoading) return;
        if (isGetServiceCodesSuccess && serviceCodesData) {
            setServiceCodes(serviceCodesData.data);
        }
    }, [isGetServiceCodesSuccess]);

    useEffect(() => {
        setTotalMembers(0);
    }, [isArchived]);

    useEffect(() => {
        if (isToggleArchiveMemberSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleArchiveMemberSuccess]);
    

    /** Actifs ↔ archivés : chaque liste garde sa propre mémoire (recherche, page), on repart donc d'une URL vierge. */
    const toggleArchived = () => {
        setSearchParams({}, { replace: true });
        setIsArchived((prev) => !prev);
    };

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalMembers(metadata.total);
        }
    };

    const handleToggleArchiveMember = (id: number, archive: boolean) => {
        MySwal.fire({
            title: archive ? 'Êtes-vous sûr de vouloir archiver cet adhérent ?' : 'Êtes-vous sûr de vouloir désarchiver cet adhérent ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                toggleArchiveMember({ id, archived: archive });
            }
        })
    }

    const handleToggleArchiveMembers = (lines: MemberTable[], archive: boolean) => {
        MySwal.fire({
            title: archive ? 'Êtes-vous sûr de vouloir archiver ces adhérents ?' : 'Êtes-vous sûr de vouloir désarchiver ces adhérents ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    toggleArchiveMember({ id: line.id, archived: archive });
                });
            }
        })
    }

    return {
        handleToggleArchiveMember,
        handleToggleArchiveMembers,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        mutationHook,
        toggleArchived,
        totalMembers,
        stateCodes,
        organizationCodes,
        serviceCodes,
        isLoading: isToggleArchiveMemberLoading,
        needReload,
        exportMutationHook,
        user
    };
};
